import Delete from '@mui/icons-material/Delete';
import Edit from '@mui/icons-material/Edit';
import { Box, IconButton, Typography as MuiTypography } from '@mui/material';
import React, { MouseEvent } from 'react';

import { COLORS } from '../../../configs/colors';

const eyebrowSx = {
  fontSize: '11px',
  fontWeight: 700,
  letterSpacing: '0.04em',
  textTransform: 'uppercase' as const,
  color: 'text.secondary',
  lineHeight: 1.4,
};

interface AddressContactRowProps {
  /** The address or contact type, shown as an eyebrow above the detail line. */
  type?: string;
  detail: string;
  showDivider: boolean;
  canEdit: boolean;
  onEdit: (event: MouseEvent<HTMLButtonElement>) => void;
  /** Omitted for a single-address layout, which has nothing to delete down to. */
  onDelete?: (event: MouseEvent<HTMLButtonElement>) => void;
  /** A word in `detail` the system does not recognise — see `UnmatchedTerm`. */
  unmatchedTerm?: string;
  /** Why that word is marked, read out to screen readers and shown on hover. */
  unmatchedReason?: string;
}

/**
 * Marks the one word in a line that the system does not recognise.
 *
 * A wavy amber underline, because that is already what a wavy underline means:
 * the reader does not have to be told it signals "unrecognised, here are some
 * alternatives" — they have seen it in every text field they have ever used.
 * The `<mark>` element carries the meaning for anything not reading the colour.
 */
const UnmatchedTerm: React.FC<{ text: string; reason?: string }> = ({ text, reason }) => (
  <Box
    component="mark"
    title={reason}
    // `title` alone is announced inconsistently and is unreachable by keyboard
    // or touch, so the reason is also the element's accessible name.
    aria-label={reason ? `${text} — ${reason}` : undefined}
    sx={{
      bgcolor: 'transparent',
      color: 'inherit',
      textDecoration: 'underline wavy',
      textDecorationColor: COLORS.TagAmberText,
      textUnderlineOffset: '3px',
      fontWeight: 600,
    }}
  >
    {text}
  </Box>
);

/**
 * Splits a line around the first occurrence of `term`, case-insensitively.
 * Returns the line untouched when the term is absent, so a stale suggestion
 * never mangles an address.
 */
export const splitAroundTerm = (
  detail: string,
  term?: string
): { before: string; match?: string; after: string } => {
  if (!term?.trim()) return { before: detail, after: '' };
  const at = detail.toLowerCase().indexOf(term.trim().toLowerCase());
  if (at === -1) return { before: detail, after: '' };
  return {
    before: detail.slice(0, at),
    match: detail.slice(at, at + term.trim().length),
    after: detail.slice(at + term.trim().length),
  };
};

/**
 * One saved address or contact.
 *
 * The actions stay visible rather than appearing on hover — they have to be
 * reachable on touch, and a row whose only affordance is hidden reads as static
 * text — but they sit muted until pointed at, so a list of five addresses is not
 * five bright blue pencils and five red bins.
 */
export const AddressContactRow: React.FC<AddressContactRowProps> = ({
  type,
  detail,
  showDivider,
  canEdit,
  onEdit,
  onDelete,
  unmatchedTerm,
  unmatchedReason,
}) => {
  const { before, match, after } = splitAroundTerm(detail, unmatchedTerm);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        py: 1,
        borderTop: showDivider ? 1 : 0,
        borderColor: 'v4.border',
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {type && <MuiTypography sx={eyebrowSx}>{type}</MuiTypography>}
        <MuiTypography sx={{ fontSize: '13px', lineHeight: 1.5, overflowWrap: 'anywhere' }}>
          {detail ? (
            <>
              {before}
              {match && <UnmatchedTerm text={match} reason={unmatchedReason} />}
              {after}
            </>
          ) : (
            '—'
          )}
        </MuiTypography>
      </Box>

      {canEdit && (
        <Box sx={{ display: 'flex', gap: 0.25, flexShrink: 0 }}>
          <IconButton
            aria-label={type ? `Edit ${type}` : 'Edit'}
            size="small"
            onClick={onEdit}
            sx={{ color: 'text.secondary', '&:hover': { color: 'v4.accentDark' } }}
          >
            <Edit sx={{ fontSize: 18 }} />
          </IconButton>
          {onDelete && (
            <IconButton
              aria-label={type ? `Delete ${type}` : 'Delete'}
              size="small"
              onClick={onDelete}
              sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}
            >
              <Delete sx={{ fontSize: 18 }} />
            </IconButton>
          )}
        </Box>
      )}
    </Box>
  );
};
